import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useBranding } from '../hooks/useBranding';
import type { SchoolBranding } from '@shared/types';

interface BrandingContextValue {
  branding: SchoolBranding | undefined;
  schoolName: string | undefined;
  schoolNameLocalized: Record<string, string> | undefined;
  isLoading: boolean;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | undefined;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

const DEFAULT_PRIMARY_COLOR = '#1e40af';
const DEFAULT_SECONDARY_COLOR = '#3b82f6';

interface BrandingProviderProps {
  children: ReactNode;
}

/**
 * BrandingProvider - Provides school branding to the entire app
 * and injects CSS custom properties for theme colors
 */
export function BrandingProvider({ children }: BrandingProviderProps) {
  const { branding, schoolName, schoolNameLocalized, isLoading } = useBranding();

  const primaryColor = branding?.primaryColor || DEFAULT_PRIMARY_COLOR;
  const secondaryColor = branding?.secondaryColor || DEFAULT_SECONDARY_COLOR;
  const accentColor = branding?.accentColor || primaryColor;

  // Inject CSS variables into document root
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-secondary', secondaryColor);
    root.style.setProperty('--brand-accent', accentColor);

    // Generate lighter/darker variants
    root.style.setProperty('--brand-primary-light', adjustColorBrightness(primaryColor, 40));
    root.style.setProperty('--brand-primary-dark', adjustColorBrightness(primaryColor, -20));
    root.style.setProperty('--brand-secondary-light', adjustColorBrightness(secondaryColor, 40));

    // Set RGB values for use with opacity
    const primaryRgb = hexToRgb(primaryColor);
    if (primaryRgb) {
      root.style.setProperty('--brand-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', primaryColor);
    }

    return () => {
      // Cleanup on unmount (optional)
    };
  }, [primaryColor, secondaryColor, accentColor]);

  const value: BrandingContextValue = {
    branding,
    schoolName,
    schoolNameLocalized,
    isLoading,
    primaryColor,
    secondaryColor,
    logoUrl: branding?.logoUrl,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook to access branding context
 */
export function useBrandingContext() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBrandingContext must be used within a BrandingProvider');
  }
  return context;
}

/**
 * Convert hex color to RGB object
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Adjust color brightness
 * @param hex - Hex color string
 * @param percent - Positive for lighter, negative for darker
 */
function adjustColorBrightness(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value: number) => {
    const adjusted = value + (255 * percent) / 100;
    return Math.min(255, Math.max(0, Math.round(adjusted)));
  };

  const r = adjust(rgb.r).toString(16).padStart(2, '0');
  const g = adjust(rgb.g).toString(16).padStart(2, '0');
  const b = adjust(rgb.b).toString(16).padStart(2, '0');

  return `#${r}${g}${b}`;
}
